import { useMemo, useState } from 'react';
import { Button, Layout as UiKitLayout } from '@anyemp/ui-kit';
import { LogOut, Moon, Sun, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import ErrorBoundary from '../ErrorBoundary';
import Avatar from '../UI/Avatar';
import { NotificationsBellWidget } from './NotificationsWidget';
import { createTalentNavigationIconProjector } from './talentNavigationIcons.js';
import {
  projectPublishedTalentDetailNavigation,
  projectGlobalNavigation,
  resolveTalentActivePath,
} from '../../utils/talentNavigationDomain.js';

const TALENT_RESPONSIVE_BREAKPOINT = 992;
const TALENT_SIDEBAR_WIDTH = 256;
const TALENT_SIDEBAR_COLLAPSED_WIDTH = 64;

function normalizeSidebarUser(user) {
  if (!user) return null;
  return {
    name: user.fullName || user.full_name || user.name || user.email,
    email: user.email,
    avatar: user.avatar,
    roles: user.roles,
  };
}

export function TalentSidebarFooter({ collapsed, mobile = false, isDarkMode, onThemeToggle, user, onLogout }) {
  const displayName = user?.name || user?.email || 'User';
  const tooltip = [displayName, user?.email, user?.roles?.length ? `Roles: ${user.roles.join(', ')}` : null]
    .filter(Boolean)
    .join('\n');
  const actionRowClass = `talent-sidebar-footer__action w-full flex items-center rounded-md px-2 py-2 text-sm font-medium ${collapsed ? 'justify-center' : 'justify-start'}`;

  return (
    <div className={`talent-sidebar-footer talent-sidebar-footer--${mobile ? 'mobile' : 'desktop'}${collapsed ? ' talent-sidebar-footer--collapsed' : ''}`}>
      <div className="talent-sidebar-footer__theme">
        <Button
          type="button"
          variant="text"
          color="neutral"
          sx={`${actionRowClass} talent-sidebar-footer__theme-button`}
          onClick={onThemeToggle}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          title={collapsed ? (isDarkMode ? 'Light Mode' : 'Dark Mode') : undefined}
        >
          {isDarkMode
            ? <Sun className={`${collapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`} aria-hidden="true" />
            : <Moon className={`${collapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`} aria-hidden="true" />}
          {!collapsed && <span className="whitespace-nowrap">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>}
        </Button>
      </div>
      <div className="talent-sidebar-footer__notifications">
        <NotificationsBellWidget collapsed={collapsed} />
      </div>
      <div className={`talent-sidebar-footer__identity flex items-center ${collapsed ? 'justify-center' : ''} ${isDarkMode ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`} title={tooltip}>
        <Avatar
          avatar={user?.avatar}
          name={user?.name}
          email={user?.email}
          size="md"
          iconSizeClasses="h-5 w-5"
          showInitialOnMissing={false}
          iconBgClassName="bg-primary-500"
          iconClassName="text-white"
        />
        {collapsed && <span className="sr-only">{tooltip}</span>}
        {!collapsed && (
          <div className="ml-3 min-w-0 flex-1 overflow-hidden text-left">
            <p className="whitespace-nowrap text-sm font-medium">{displayName}</p>
            {user?.email && <p className="whitespace-nowrap text-xs opacity-75">{user.email}</p>}
            {user?.roles?.length > 0 && <p className="whitespace-nowrap text-xs opacity-75">Roles: {user.roles.join(', ')}</p>}
          </div>
        )}
      </div>
      <div className="talent-sidebar-footer__logout">
        <Button
          type="button"
          variant="text"
          color="error"
          sx={`${actionRowClass} talent-sidebar-footer__logout-button`}
          onClick={onLogout}
          aria-label="Log out"
          title={collapsed ? 'Logout' : undefined}
        >
          <LogOut className={`${collapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`} aria-hidden="true" />
          {!collapsed && <span className="whitespace-nowrap">Logout</span>}
        </Button>
      </div>
    </div>
  );
}

export default function TalentAdaptiveShellFrame({
  children,
  locationPathname,
  isDarkMode,
  onThemeToggle,
  user,
  userPermissions,
  onLogout,
  candidateStages,
  notificationCounts,
  sidebarNav,
  backPath,
}) {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const iconProjector = useMemo(() => createTalentNavigationIconProjector(), []);
  const globalNavigation = useMemo(() => projectGlobalNavigation({
    permissions: userPermissions,
    candidateStages,
    notificationCounts,
    iconProjector,
  }).items, [candidateStages, iconProjector, notificationCounts, userPermissions]);
  const detailNavigation = useMemo(() => projectPublishedTalentDetailNavigation({
    sidebarNav,
    backPath,
    iconProjector,
  }), [backPath, iconProjector, sidebarNav]);
  const navigation = detailNavigation?.items || globalNavigation;
  const activePath = useMemo(
    () => resolveTalentActivePath(locationPathname, candidateStages),
    [candidateStages, locationPathname],
  );
  const sidebarUser = useMemo(() => normalizeSidebarUser(user), [user]);

  const handleItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
      return;
    }
    detailNavigation?.onItemClick(item.id);
  };

  const renderFooter = ({ collapsed, mobile }) => (
    <TalentSidebarFooter
      collapsed={mobile ? false : collapsed}
      mobile={mobile}
      isDarkMode={isDarkMode}
      onThemeToggle={onThemeToggle}
      user={sidebarUser}
      onLogout={onLogout}
    />
  );

  const sidebar = {
    items: navigation,
    collapsed: sidebarCollapsed,
    onCollapsedChange: setSidebarCollapsed,
    responsiveBreakpoint: TALENT_RESPONSIVE_BREAKPOINT,
    width: TALENT_SIDEBAR_WIDTH,
    collapsedWidth: TALENT_SIDEBAR_COLLAPSED_WIDTH,
    logoIcon: (
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
        <UserCircle className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    logoTitle: 'Talent Service',
    onLogoClick: () => navigate('/'),
    togglePlacement: 'bottom',
    sx: `talent-sidebar ${isDarkMode ? 'talent-sidebar--dark' : 'talent-sidebar--light'}`,
    footer: renderFooter,
    activeItemId: detailNavigation?.activeItemId,
    activePath,
    onItemClick: handleItemClick,
    parentItemBehavior: 'accordion',
  };

  return (
    <UiKitLayout
      variant="sidebar"
      responsiveBreakpoint={TALENT_RESPONSIVE_BREAKPOINT}
      sidebar={sidebar}
      mobileHeaderTitle="Talent Service"
      mobileHeaderActions={<NotificationsBellWidget collapsed />}
      sx={isDarkMode ? 'bg-dark-bg-primary' : 'bg-light-bg-secondary'}
    >
      <div className="flex min-h-screen w-full min-w-0 flex-1 flex-col px-4 py-6 sm:px-6 md:px-8">
        <ErrorBoundary>{children}</ErrorBoundary>
      </div>
    </UiKitLayout>
  );
}
